import { forwardRef } from 'react';
import { AlertSeverity } from '@/constants/alertTypes.tsx';
import { Alert as MuiAlert, AlertTitle as MuiAlertTitle, Typography, styled } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { assertNever } from '@/utils/assertNever.ts';
import { Typographies } from '@/schemas/sceneDataSchema';
import { RichText } from './RichText';
import { TypographyAlign, TypographyVariants } from '@/constants/typographyTypes';
import { RouteFn } from '@/hooks/use-flow';

interface AlertProps {
  onClose?: () => void;
  severity: AlertSeverity;
  title: string | null;
  titleGutterBottom: boolean | null;
  text: string | null;
  spans?: Typographies[] | null;
  route?: RouteFn;
}

const severityMain = (theme: Theme, s: AlertSeverity): string => {
  switch (s) {
    case AlertSeverity.ERROR:
      return theme.palette.error.main;
    case AlertSeverity.WARNING:
      return theme.palette.warning.main;
    case AlertSeverity.INFO:
      return theme.palette.info.main;
    case AlertSeverity.SUCCESS:
      return theme.palette.success.main;
    default:
      return assertNever(s);
  }
};

export const Alert = forwardRef<HTMLDivElement, AlertProps>(function Alert(
  { onClose, severity, title, titleGutterBottom, text, spans, route }: AlertProps,
  ref
) {
  return (
    <StyledAlert
      ref={ref}
      severity={severity}
      onClose={onClose ?? undefined}
    >
      {title && (
        <MuiAlertTitle gutterBottom={titleGutterBottom ?? true}>
          <Typography
            variant='h4'
            component='span'
          >
            {title}
          </Typography>
        </MuiAlertTitle>
      )}
      {text && <Typography variant='body2'>{text}</Typography>}

      {spans && (
        <RichText
          spans={spans}
          align={TypographyAlign.LEFT}
          variant={TypographyVariants.BODY2}
          route={route}
        />
      )}
    </StyledAlert>
  );
});

const StyledAlert = styled(MuiAlert)`
  border: 1px solid ${({ theme, severity }) => severityMain(theme, severity as AlertSeverity)};
`;
